import { useState, useEffect, useCallback } from 'react';

/**
 * Dirty-safe draft state hook for loan-derived local fields.
 *
 * - Clean value adopts new source updates automatically.
 * - Dirty value ignores incoming source changes.
 * - markClean() clears dirty flag after a successful save.
 * - reset() discards the draft and re-adopts the latest source value.
 */
export function useSyncedDraftField<T>(sourceValue: T): {
  value: T;
  setValue: (v: T | ((prev: T) => T)) => void;
  isDirty: boolean;
  markClean: () => void;
  reset: () => void;
} {
  const [value, setValueRaw] = useState<T>(sourceValue);
  const [isDirty, setIsDirty] = useState(false);

  // Dirty-safe prop sync: only adopt new source when NOT dirty.
  // isDirty intentionally excluded from deps — only sync when clean.
  useEffect(() => {
    if (!isDirty) {
      setValueRaw(sourceValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceValue]);

  const setValue = useCallback((v: T | ((prev: T) => T)) => {
    setValueRaw(v);
    setIsDirty(true);
  }, []);

  const markClean = useCallback(() => setIsDirty(false), []);

  const reset = useCallback(() => {
    setValueRaw(sourceValue);
    setIsDirty(false);
     
  }, [sourceValue]);

  return { value, setValue, isDirty, markClean, reset };
}
